import "./style.scss";

import Basket from "assets/img/svg/basket";
import ModalBox from "components/Modal";
import DeleteModal from "components/shared/DeleteModal";
import ImageFormComponent from "components/shared/FormComponent/ImageFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import StyledButton from "components/shared/StyledButton";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IBrand } from "store/brandStore";
import { deleteBrand, fetchBrands, postBrand, putBrand } from "store/brandStore";
import { useAppDispatch, useAppSelector } from "store/store";

interface MultipleBrandSettingsProps {}

const MultipleBrandSettings = (props: MultipleBrandSettingsProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();
    const { sendPictureStatus } = useAppSelector((state) => state.titles);
    const { brands } = useAppSelector((state) => state.brands);
    const [showCreateBrandModal, setShowCreateBrandModal] = useState(false);
    const [currentBrand, setCurrentBrand] = useState<IBrand | undefined>();
    const [showModalDelete, setShowModalDelete] = useState(false);

    const cleanedBrands = brands.map((brand) => {
        // -1 is the id of the head publishing house.
        if (brand.id === -1) {
            return { ...brand, name: companyInfos?.name };
        } else {
            return brand;
        }
    });

    useEffect(() => {
        dispatch(fetchBrands(true));
    }, []);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            avatarUrl: "",
            name: "",
        },
        mode: "all",
    });

    const hasErrors = Object.keys(errors).length > 0;

    const onSubmit = async (formData: any) => {
        const data: any = {};
        data.avatarUrl = formData.avatarUrl ?? null;
        data.name = formData.name ?? "";
        if (!currentBrand) {
            dispatch(postBrand({ data })).then((resp) => {
                setShowCreateBrandModal(false);
                setCurrentBrand(undefined);
                reset();
            });
        } else {
            currentBrand.id &&
                dispatch(putBrand({ data, id: currentBrand.id })).then((resp) => {
                    setShowCreateBrandModal(false);
                    setCurrentBrand(undefined);
                    reset();
                });
        }
    };

    const handleDelete = async (id?: number) => {
        setShowModalDelete(false);
        if (id) await dispatch(deleteBrand({ id }));
        setShowCreateBrandModal(false);
        setCurrentBrand(undefined);
        reset();
    };

    const handleOpenDeleteModal = (e: any, row: any) => {
        e.stopPropagation();
        setCurrentBrand(row);
        setShowModalDelete(true);
        setValue("name", row.name);
        setValue("avatarUrl", row.avatarUrl);
    };

    const brandColumns: LastElementTableColumns[] = [
        { id: "name", label: t("Brand.Name"), align: "left" },
        {
            id: "associatedTitles",
            label: t("Brand.AssociatedTitles"),
            align: "left",
            format: (value: any) => `${value ?? 0} ${t("Brand.AssociatedTitles", { count: +value })}`,
        },
        {
            id: "actions",
            label: "",
            align: "right",
            format: (value: any, id?: number, row?: any) =>
                id && id > -1 && <Basket onClick={(e: any) => handleOpenDeleteModal(e, row)} />,
        },
    ];

    const handleEditBrand = (row: any) => {
        setCurrentBrand(row);
        setShowCreateBrandModal(true);
        setValue("name", row.name);
        setValue("avatarUrl", row.avatarUrl);
    };

    const handleModalClose = () => {
        setShowCreateBrandModal(false);
        setCurrentBrand(undefined);
        reset();
    };

    return (
        <div className="multiple-brand-settings">
            <ModalBox
                open={showCreateBrandModal}
                onClose={() => handleModalClose()}
                mainTitle={currentBrand ? t("Brand.Update") : t("Brand.Create")}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="create-brand">
                        <ImageFormComponent
                            label={t("Brand.Avatar")}
                            avatar={watch("avatarUrl")}
                            setAvatar={(name: string, value: any) => setValue("avatarUrl", value)}
                            sendPictureStatus={sendPictureStatus}
                            control={control}
                            controlName="avatarUrl"
                            type="brand"
                        />
                        <TextFormComponent
                            label={t("Brand.Name")}
                            placeholder={""}
                            control={control}
                            controlName="name"
                            error={errors.name}
                            required
                        />
                        <StyledButton buttonType="primary" disabled={hasErrors} type="submit">
                            {t("Brand.Validate")}
                        </StyledButton>
                    </div>
                </form>
            </ModalBox>

            <DeleteModal
                elementTitle={`"${currentBrand?.name ?? ""}"`}
                open={showModalDelete}
                onClose={() => setShowModalDelete(false)}
                onDelete={() => handleDelete(currentBrand?.id)}
            />

            <div className="multiple-brand-settings-header">
                <h1>{t("Settings.Brands")}</h1>
                <StyledButton buttonType="primary" onClick={() => setShowCreateBrandModal(true)}>
                    {t("Brand.Create")}
                </StyledButton>
            </div>
            <div className="multiple-brand-settings-list">
                <LastElementTable
                    columns={brandColumns}
                    data={cleanedBrands}
                    bottomFooterContent={<></>}
                    onRowClick={(id: any, id2: any, type: any, row: any) => id && id > -1 && handleEditBrand(row)}
                    max={-1}
                    dataType="brands"
                />
            </div>
        </div>
    );
};

export default MultipleBrandSettings;
